import { useEffect, useState, type ReactNode } from "react";

import type { UseCameraState } from "../hooks/useCamera.js";

/**
 * Tỉ lệ khung của stream, dạng chuỗi CSS `"w / h"` — `null` khi chưa có metadata.
 *
 * Nghe cả `resize`, không chỉ `loadedmetadata`: đổi camera trước/sau hay quay ngang điện thoại
 * làm `videoWidth/videoHeight` đổi trên **cùng** một element, và khi đó `loadedmetadata` không bắn
 * lại — thiếu `resize` thì khung giữ tỉ lệ cũ và ảnh bị cắt.
 */
function useStreamAspectRatio(videoRef: React.RefObject<HTMLVideoElement | null>): string | null {
  const [ratio, setRatio] = useState<string | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const sync = (): void => {
      const { videoWidth, videoHeight } = video;
      if (videoWidth > 0 && videoHeight > 0) setRatio(`${videoWidth} / ${videoHeight}`);
    };

    sync();
    video.addEventListener("loadedmetadata", sync);
    video.addEventListener("resize", sync);
    return () => {
      video.removeEventListener("loadedmetadata", sync);
      video.removeEventListener("resize", sync);
    };
  }, [videoRef]);

  return ratio;
}

export interface CameraPreviewProps {
  camera: UseCameraState;
  /** Overlay (skeleton canvas ở Bước 7, badge trạng thái ở Bước 15). */
  children?: ReactNode;
}

/**
 * §10, §24 — preview camera.
 *
 * `playsInline` + `muted` + `autoPlay` là **bắt buộc** trên iOS Safari: thiếu `playsInline`,
 * video tự nhảy sang fullscreen native và đè lên toàn bộ UI; thiếu `muted`, autoplay bị chặn.
 *
 * Mirror (`preview--mirrored`) **chỉ ở tầng hiển thị**. Landmark đưa vào engine giữ nguyên toạ độ
 * gốc: nếu mirror cả dữ liệu, leftShoulder/rightShoulder bị đảo → `shoulderTilt` lật dấu, baseline
 * vẫn hấp thụ được offset nên bug không lộ ra ngay, mà chỉ biểu hiện thành nhắc sai bên vai.
 *
 * Khung được đặt theo tỉ lệ THẬT của stream chứ không phải một tỉ lệ cố định trong CSS: mọi
 * overlay (skeleton ở §25, badge trạng thái ở §15) vẽ theo toạ độ chuẩn hoá trên khung này, nên
 * khung lệch tỉ lệ so với stream thì `object-fit: cover` cắt ảnh và overlay lệch khỏi người.
 */
export function CameraPreview({ camera, children }: CameraPreviewProps): React.JSX.Element {
  const mirrored = camera.facingMode === "user";
  const aspectRatio = useStreamAspectRatio(camera.videoRef);
  return (
    <div className="preview" style={aspectRatio ? { aspectRatio } : undefined}>
      <video
        ref={camera.videoRef}
        className={`preview__video${mirrored ? " preview--mirrored" : ""}`}
        playsInline
        muted
        autoPlay
      />
      {children}
      {camera.status === "error" ? (
        <div className="preview__error" role="alert">
          <p>{camera.errorMessage}</p>
          <button type="button" className="btn btn--secondary" onClick={camera.start}>
            Thử lại
          </button>
        </div>
      ) : null}
    </div>
  );
}
